import React, { useRef, useEffect, useCallback } from 'react';

const DualSlider = ({ min, max, step, minValue, maxValue, onMinChange, onMaxChange, label, unit }) => {
    const minValRef = useRef(minValue);
    const maxValRef = useRef(maxValue);
    const range = useRef(null);

    // Convert to percentage
    const getPercent = useCallback((value) => Math.round(((value - min) / (max - min)) * 100), [min, max]);

    // Update the range bar when either value changes
    useEffect(() => {
        if (range.current) {
            const minPercent = getPercent(minValue);
            const maxPercent = getPercent(maxValue);
            
            range.current.style.left = `${minPercent}%`;
            range.current.style.width = `${maxPercent - minPercent}%`;
        }
    }, [minValue, maxValue, getPercent]);

    // Update refs
    useEffect(() => {
        minValRef.current = minValue;
        maxValRef.current = maxValue;
    }, [minValue, maxValue]);

    const handleMinChange = (e) => {
        const value = Math.min(Number(e.target.value), maxValue - step);
        minValRef.current = value;
        onMinChange(value);
    };

    const handleMaxChange = (e) => {
        const value = Math.max(Number(e.target.value), minValue + step);
        maxValRef.current = value;
        onMaxChange(value);
    };

    return (
        <div className="mb-8">
            {label && (
                <div className="flex items-center justify-between mb-4">
                    <label className="text-sm font-medium text-custom-light-grey">{label}</label>
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-custom-light-blue">
                            {minValue}{unit}
                        </span>
                        <span className="text-sm text-custom-light-grey">-</span>
                        <span className="text-sm font-semibold text-custom-light-blue">
                            {maxValue}{unit}
                        </span>
                    </div>
                </div>
            )}
            
            <div className="relative w-full h-12 flex items-center">
                {/* Track background */}
                <div className="absolute w-full h-[2px] bg-custom-charcoal rounded-full" />
                
                {/* Active range */}
                <div 
                    ref={range}
                    className="absolute h-[2px] bg-custom-light-blue rounded-full"
                />
                
                {/* Min slider */}
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={minValue}
                    onChange={handleMinChange}
                    className="absolute w-full h-2 appearance-none bg-transparent pointer-events-none z-10
                        [&::-webkit-slider-thumb]:appearance-none
                        [&::-webkit-slider-thumb]:w-5
                        [&::-webkit-slider-thumb]:h-5
                        [&::-webkit-slider-thumb]:rounded-full
                        [&::-webkit-slider-thumb]:bg-custom-light-blue
                        [&::-webkit-slider-thumb]:cursor-pointer
                        [&::-webkit-slider-thumb]:pointer-events-auto
                        [&::-webkit-slider-thumb]:border-4
                        [&::-webkit-slider-thumb]:border-custom-dark
                        [&::-webkit-slider-thumb]:shadow-lg
                        [&::-webkit-slider-thumb]:transition-all
                        [&::-webkit-slider-thumb]:hover:scale-110
                        [&::-moz-range-thumb]:w-5
                        [&::-moz-range-thumb]:h-5
                        [&::-moz-range-thumb]:rounded-full
                        [&::-moz-range-thumb]:bg-custom-light-blue
                        [&::-moz-range-thumb]:cursor-pointer
                        [&::-moz-range-thumb]:pointer-events-auto
                        [&::-moz-range-thumb]:shadow-lg
                        [&::-moz-range-thumb]:transition-all
                        [&::-moz-range-thumb]:hover:scale-110"
                />
                
                {/* Max slider */}
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={maxValue}
                    onChange={handleMaxChange}
                    className="absolute w-full h-2 appearance-none bg-transparent pointer-events-none z-20
                        [&::-webkit-slider-thumb]:appearance-none
                        [&::-webkit-slider-thumb]:w-5
                        [&::-webkit-slider-thumb]:h-5
                        [&::-webkit-slider-thumb]:rounded-full
                        [&::-webkit-slider-thumb]:bg-custom-light-blue
                        [&::-webkit-slider-thumb]:cursor-pointer
                        [&::-webkit-slider-thumb]:pointer-events-auto
                        [&::-webkit-slider-thumb]:border-4
                        [&::-webkit-slider-thumb]:border-custom-dark
                        [&::-webkit-slider-thumb]:shadow-lg
                        [&::-webkit-slider-thumb]:transition-all
                        [&::-webkit-slider-thumb]:hover:scale-110
                        [&::-moz-range-thumb]:w-5
                        [&::-moz-range-thumb]:h-5
                        [&::-moz-range-thumb]:rounded-full
                        [&::-moz-range-thumb]:bg-custom-light-blue
                        [&::-moz-range-thumb]:cursor-pointer
                        [&::-moz-range-thumb]:pointer-events-auto
                        [&::-moz-range-thumb]:shadow-lg
                        [&::-moz-range-thumb]:transition-all
                        [&::-moz-range-thumb]:hover:scale-110"
                />
            </div>

            {/* Scale markers */}
            <div className="flex justify-between mt-2 px-1">
                {Array.from({ length: 5 }, (_, i) => {
                    const value = min + (max - min) * (i / 4);
                    return (
                        <span key={i} className="text-xs text-custom-light-grey/50">
                            {Math.round(value)}{unit}
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

export default DualSlider;